import React, { FC, useEffect, useState } from 'react';
import { LoadingState } from 'types/enums';
import { useAppSelector } from 'app/store';
import {
  getApiKeyWebLoadingState,
  getBootstrapLoadingState,
} from 'app/store/Auth/selectors';
import { useLocation } from 'react-router-dom';
import { ErrorBoundaryMessage } from './ErrorBoundaryMessage';

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  didAppHang?: boolean;
  hasError: boolean;
  setHasError: (boolean) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundaryComponent extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { hasError } = this.props;
    if (!hasError && prevProps.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch() {
    const { setHasError } = this.props;
    setHasError(true);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    const { children, didAppHang } = this.props;
    const { hasError } = this.state;

    if (hasError || didAppHang) {
      return (
        <ErrorBoundaryMessage
          onTryAgain={() => {
            if (didAppHang) {
              window.location.reload();
            } else {
              this.setState({ hasError: false });
            }
          }}
        />
      );
    }
    return children;
  }
}

export const ErrorBoundary: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [hasError, setHasError] = useState(false);
  const location = useLocation();

  const apiKeyLoadingState = useAppSelector(getApiKeyWebLoadingState);
  const bootstrapLoadingState = useAppSelector(getBootstrapLoadingState);
  const didAppHang =
    apiKeyLoadingState === LoadingState.ERROR ||
    bootstrapLoadingState === LoadingState.ERROR;

  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <ErrorBoundaryComponent
      didAppHang={didAppHang}
      hasError={hasError}
      setHasError={setHasError}
    >
      {children}
    </ErrorBoundaryComponent>
  );
};
